import { Directive, HostListener } from '@angular/core';
import { ProductHero } from './product-hero.data';

// One store picture: its name (under it, and in the lightbox), the page's file, the lightbox's
// full-size file (loaded only then) and the alt text.
export interface Shot {
  name: string;
  src: string;
  large: string;
  alt: string;
}

// The pictures of an Apps & Plugins page (../product-page.scss): the product itself on its stage
// at the top (`hero`, from product-hero.data.ts), and the store pictures under Screenshots, each
// opening in the theme pages' lightbox (global src/styles/page/tool-lightbox.scss), where the
// arrows and the arrow keys page through them all and Escape closes it. A store picture that is
// also the hero (Dock Town's menu bar town) stays out of the gallery and opens from the stage.
//
// The lightbox is a dialog for the keyboard too: opening it moves focus to its Close button,
// Tab stays inside its pill, and closing it puts focus back on the picture that opened it.
@Directive()
export abstract class ProductShots {

  abstract readonly hero: ProductHero;
  abstract readonly shots: Shot[];

  // The picture enlarged in the lightbox, or null when it's closed.
  lightbox: Shot | null = null;

  // The element that opened the lightbox, to give focus back to on close.
  private opener: HTMLElement | null = null;

  /** The store pictures under Screenshots: every shot but the one on the stage. */
  get screenshots(): Shot[] {
    return this.shots.filter( s => s.src !== this.hero.src );
  }

  /** The store picture on the stage, when the hero is one of them (so it can open in the lightbox). */
  get heroShot(): Shot | undefined {
    return this.shots.find( s => s.src === this.hero.src );
  }

  /** Whether any lightbox of the page is open (a page may have a second one, as All In One does). */
  protected get lightboxOpen(): boolean {
    return !!this.lightbox;
  }

  openLightbox( shot: Shot ): void {
    this.rememberOpener();
    this.lightbox = shot;
    this.focusLightbox();
  }

  closeLightbox(): void {
    this.lightbox = null;
    this.restoreOpener();
  }

  /** The picture `step` places from the one in the lightbox, wrapping round. */
  shotAt( step: number ): Shot {
    const count = this.shots.length;
    const index = this.lightbox ? this.shots.indexOf( this.lightbox ) : 0;
    return this.shots[ ( ( index + step ) % count + count ) % count ];
  }

  /** Page the lightbox to the previous (-1) or next (+1) picture. */
  stepLightbox( dir: number, ev?: Event ): void {
    ev?.stopPropagation();
    if ( this.lightbox ) {
      this.lightbox = this.shotAt( dir );
    }
  }

  @HostListener( 'document:keydown.arrowleft' )
  onShotsArrowLeft(): void {
    if ( this.lightbox ) { this.stepLightbox( -1 ); }
  }

  @HostListener( 'document:keydown.arrowright' )
  onShotsArrowRight(): void {
    if ( this.lightbox ) { this.stepLightbox( 1 ); }
  }

  @HostListener( 'document:keydown.escape' )
  onShotsEscape(): void {
    if ( this.lightbox ) { this.closeLightbox(); }
  }

  // Tab cycles through the pill's buttons while a lightbox is open, instead of walking the page
  // behind the backdrop.
  @HostListener( 'document:keydown.tab', [ '$event' ] )
  @HostListener( 'document:keydown.shift.tab', [ '$event' ] )
  onShotsTab( event: KeyboardEvent ): void {
    if ( !this.lightboxOpen ) { return; }
    const buttons = Array.from( document.querySelectorAll<HTMLElement>( '.cp-lightbox-caption-row button' ) );
    if ( !buttons.length ) { return; }
    const index = buttons.indexOf( document.activeElement as HTMLElement );
    const next = event.shiftKey
      ? ( index <= 0 ? buttons.length - 1 : index - 1 )
      : ( index < 0 || index === buttons.length - 1 ? 0 : index + 1 );
    event.preventDefault();
    buttons[ next ].focus();
  }

  /** Keep the element that is about to open a lightbox, so focus can go back to it. */
  protected rememberOpener(): void {
    this.opener = typeof document !== 'undefined' ? document.activeElement as HTMLElement : null;
  }

  /** Move focus into the lightbox's pill (its Close button) once it has rendered. */
  protected focusLightbox(): void {
    if ( typeof document === 'undefined' ) { return; }
    setTimeout( () => document.querySelector<HTMLElement>( '.cp-lightbox-caption-row .cp-lb-close' )?.focus() );
  }

  /** Give focus back to the element that opened the lightbox. */
  protected restoreOpener(): void {
    const opener = this.opener;
    this.opener = null;
    if ( opener && typeof opener.focus === 'function' ) {
      setTimeout( () => opener.focus( { preventScroll: true } ) );
    }
  }
}
